import { useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Building2, Globe2, ChevronRight } from 'lucide-react';

const LocationSidebar = ({
  locations = [],
  selectedLocation,
  hoveredLocationId,
  onLocationSelect,
  onLocationHover,
  maxHeight = '500px'
}) => {
  const listRef = useRef(null);
  const itemRefs = useRef({});

  // Scroll to selected location
  useEffect(() => {
    if (selectedLocation && itemRefs.current[selectedLocation.id]) {
      itemRefs.current[selectedLocation.id].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedLocation]);

  // Group locations by continent for better organization
  const groupedLocations = useMemo(() => {
    const groups = {};
    locations.forEach(loc => {
      const continent = loc.continent || 'Other';
      if (!groups[continent]) {
        groups[continent] = [];
      }
      groups[continent].push(loc);
    });
    return groups;
  }, [locations]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'country':
        return <Globe2 className="w-3.5 h-3.5" />;
      case 'state':
      case 'province':
      case 'territory':
        return <Building2 className="w-3.5 h-3.5" />;
      default:
        return <MapPin className="w-3.5 h-3.5" />;
    }
  };

  const getStatusStyles = (status) => {
    if (status === 'active') {
      return 'bg-green-100 text-green-700 border-green-200';
    }
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  if (locations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <MapPin className="w-12 h-12 mb-3 opacity-30" />
        <p className="text-sm font-medium">No locations found</p>
        <p className="text-xs mt-1">Try adjusting your search or filters</p>
      </div>
    );
  }

  return (
    <div 
      ref={listRef}
      className="overflow-y-auto overflow-x-hidden custom-scrollbar"
      style={{ maxHeight }}
    >
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>

      <div className="space-y-1 pr-2">
        <AnimatePresence mode="popLayout">
          {locations.map((location, index) => {
            const isSelected = selectedLocation?.id === location.id;
            const isHovered = hoveredLocationId === location.id;
            
            return (
              <motion.div
                key={location.id}
                ref={el => itemRefs.current[location.id] = el}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.15, delay: Math.min(index * 0.02, 0.3) }}
                onClick={() => onLocationSelect?.(location)}
                onMouseEnter={() => onLocationHover?.(location.id)}
                onMouseLeave={() => onLocationHover?.(null)}
                className={`
                  group relative px-3 py-2.5 rounded-lg cursor-pointer
                  transition-all duration-200 ease-out
                  ${isSelected 
                    ? 'bg-blue-50 border border-blue-200 shadow-sm' 
                    : isHovered 
                      ? 'bg-gray-50 border border-gray-200' 
                      : 'border border-transparent hover:bg-gray-50'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  {/* Flag */}
                  <span className="text-xl leading-none mt-0.5 flex-shrink-0">
                    {location.flag}
                  </span>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className={`
                        font-medium text-sm truncate
                        ${isSelected ? 'text-blue-900' : 'text-gray-800'}
                      `}>
                        {location.name}
                      </h4>
                      
                      {/* Active indicator */}
                      {location.status === 'active' && (
                        <span className="relative flex h-2 w-2 flex-shrink-0">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                      )}
                    </div>
                    
                    {/* Meta info */}
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`
                        inline-flex items-center gap-1 text-xs
                        ${isSelected ? 'text-blue-600' : 'text-gray-500'}
                      `}>
                        {getTypeIcon(location.type)}
                        <span className="capitalize">{location.type}</span>
                      </span>
                      
                      <span className="text-gray-300">•</span>
                      
                      <span className={`
                        text-xs
                        ${isSelected ? 'text-blue-600' : 'text-gray-500'}
                      `}>
                        {location.continent}
                      </span>
                    </div>
                  </div>

                  {/* Arrow indicator */}
                  <ChevronRight className={`
                    w-4 h-4 flex-shrink-0 mt-1 transition-all duration-200
                    ${isSelected 
                      ? 'text-blue-500 translate-x-0 opacity-100' 
                      : 'text-gray-300 -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'
                    }
                  `} />
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LocationSidebar;
